import { z } from 'zod/v3';

const ColumnRefSchema = z.object({
  table: z.string(),
  columns: z.array(z.string()).min(1),
});

const UniqueKeySchema = z.object({
  name: z.string().optional(),
  columns: z.array(z.string()).min(1),
  where: z.string().optional(),
});

const RelationshipSchema = z.object({
  from: ColumnRefSchema,
  to: ColumnRefSchema,
  kind: z.enum(['foreign_key', 'logical']),
  cardinality: z.enum(['many_to_one', 'one_to_one', 'one_to_zero_or_one']),
  onDelete: z.enum(['restrict']).optional(),
  note: z.string(),
});

const NotAJoinSchema = z.object({
  from: ColumnRefSchema,
  notTo: ColumnRefSchema,
  reason: z.string(),
});

const EnumDomainSchema = z.object({
  column: z.string(),
  values: z.array(z.string()).min(1),
  isClosed: z.boolean(),
});

const MeasureSchema = z.object({
  id: z.string(),
  label: z.string(),
  sql: z.string(),
  synonyms: z.array(z.string()),
  note: z.string().optional(),
});

const CaveatSchema = z.object({
  kind: z.enum([
    'backfill_null',
    'remint_duplicates',
    'no_foreign_key',
    'partial_unique',
    'sentinel_value',
    'misleading_name',
    'late_fill',
  ]),
  columns: z.array(z.string()),
  text: z.string(),
});

const ColumnSchema = z.object({
  name: z.string(),
  postgresType: z.string(),
  isNullable: z.boolean(),
  definition: z.string(),
});

export const AnalyticsSemanticTableSchema = z.object({
  schema: z.literal('public'),
  table: z.string(),
  domain: z.enum(['sequence', 'ingest', 'copilot', 'memory']),
  grain: z.string().min(1),
  primaryKey: z.array(z.string()).min(1),
  uniqueKeys: z.array(UniqueKeySchema),
  relationships: z.array(RelationshipSchema),
  notJoins: z.array(NotAJoinSchema),
  enumDomains: z.array(EnumDomainSchema),
  measures: z.array(MeasureSchema),
  caveats: z.array(CaveatSchema),
  writtenBy: z.array(
    z.enum(['live_projection', 'sequence_backfill', 'copilot_cost_job']),
  ),
  columns: z.array(ColumnSchema).min(1),
});

export const AnalyticsSemanticModelSchema = z.object({
  version: z.literal(1),
  tables: z.array(AnalyticsSemanticTableSchema),
});

export type AnalyticsSemanticModel = z.infer<
  typeof AnalyticsSemanticModelSchema
>;
export type AnalyticsSemanticTable = z.infer<
  typeof AnalyticsSemanticTableSchema
>;
export type AnalyticsSemanticColumn = z.infer<typeof ColumnSchema>;
export type AnalyticsSemanticRelationship = z.infer<typeof RelationshipSchema>;
export type AnalyticsSemanticCaveat = z.infer<typeof CaveatSchema>;
